import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { blogPosts } from "../data.js";
import { localizeBody, localizeField } from "../i18n.js";
import { BodyRenderer } from "./BodyRenderer.jsx";
import CopyLinkButton from "./CopyLinkButton.jsx";

export default function BlogPage({ t, copy, language }) {
  const { blogId } = useParams();
  const blog = blogPosts[blogId];
  const progress = useReadingProgress(blogId);

  if (!blog) {
    return <div style={{ color: t.dim }}>{copy.blogNotFound}</div>;
  }

  return (
    <article>
      <div
        aria-hidden="true"
        className="reading-progress"
        style={{
          background: t.link,
          transform: `scaleX(${progress})`,
        }}
      />

      <Link
        to="/blog"
        style={{
          color: t.dim,
          display: "inline-block",
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 12,
          marginBottom: 20,
          textDecoration: "none",
        }}
      >
        {copy.backBlog}
      </Link>

      <h2 style={{ fontSize: 23, fontWeight: 400, color: t.text, marginBottom: 8 }}>
        {localizeField(blog, "title", language)}
      </h2>

      <div style={metaStyle}>
        <span style={{ color: t.dim }}>{blog.date}</span>
        <CopyLinkButton t={t} copy={copy} />
        {blog.links.map(([label, href]) =>
          label.toLowerCase().includes("private") ? (
            <span key={label} style={{ color: t.dim }}>
              {copy.private}
            </span>
          ) : (
            <a
              key={label}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: t.link, textDecoration: "none" }}
            >
              {label} -&gt;
            </a>
          )
        )}
      </div>

      <BodyRenderer body={localizeBody(blog, language)} t={t} copy={copy} />
    </article>
  );
}

function useReadingProgress(resetKey) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    function updateProgress() {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;

      if (scrollHeight <= 0) {
        setProgress(1);
        return;
      }

      setProgress(Math.min(Math.max(scrollTop / scrollHeight, 0), 1));
    }

    updateProgress();
    window.addEventListener("scroll", updateProgress, { passive: true });
    window.addEventListener("resize", updateProgress);

    return () => {
      window.removeEventListener("scroll", updateProgress);
      window.removeEventListener("resize", updateProgress);
    };
  }, [resetKey]);

  return progress;
}

const metaStyle = {
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: 12,
  marginBottom: 28,
  display: "flex",
  gap: 20,
  flexWrap: "wrap",
};
